import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { Camera, ImagePlus, X } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { cn } from '@shared/lib/cn';

const ACCEPT = 'image/jpeg,image/png,image/webp';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface SelfieUploadFieldProps {
  value: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  className?: string;
}

/**
 * Visitor selfie picker with preview — used on public accept and member selfie upload.
 */
export function SelfieUploadField({
  value,
  onChange,
  error,
  className,
}: SelfieUploadFieldProps): JSX.Element {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const openPicker = () => inputRef.current?.click();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    onChange(file);
    event.target.value = '';
  };

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-label font-semibold text-ink-secondary">Visitor selfie</span>
        <span className="text-caption text-ink-muted">Required</span>
      </div>

      {value && previewUrl ? (
        <div className="rounded-xl bg-surface p-4 hairline">
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <div className="h-36 w-36 overflow-hidden rounded-full ring-2 ring-primary-600/25 ring-offset-2 ring-offset-surface">
                <img
                  src={previewUrl}
                  alt="Selfie preview"
                  className="h-full w-full object-cover"
                />
              </div>
              <button
                type="button"
                aria-label="Remove selfie"
                className="absolute -right-1 -top-1 flex h-8 w-8 items-center justify-center rounded-full bg-card text-ink shadow-md hairline transition-colors hover:bg-sunken"
                onClick={handleRemove}
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>

            <p className="mt-3 max-w-full truncate text-body-sm font-medium text-ink">{value.name}</p>
            <p className="text-caption text-ink-muted">{formatFileSize(value.size)}</p>

            <div className="mt-4 flex w-full gap-2">
              <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={openPicker}>
                <Camera className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                Change photo
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={handleRemove}>
                Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className={cn(
            'group flex w-full flex-col items-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
            'border-hairline-strong bg-elevated hover:border-primary-600/40 hover:bg-primary-600/5',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card',
            error && 'border-danger/50 bg-danger/5',
          )}
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-600/10 text-primary-700 transition-colors group-hover:bg-primary-600/15">
            <Camera className="h-8 w-8" strokeWidth={1.5} aria-hidden />
          </span>
          <span className="mt-4 text-body font-semibold text-ink">Take or upload a selfie</span>
          <span className="mt-1 max-w-[16rem] text-caption leading-relaxed text-ink-muted">
            Use a clear, front-facing photo of your face for gate verification
          </span>
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1 text-caption font-medium text-ink-secondary hairline">
            <ImagePlus className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            JPG, PNG or WebP
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT}
        capture="user"
        className="sr-only"
        aria-label="Visitor selfie photo"
        aria-describedby={error ? `${inputId}-error` : `${inputId}-helper`}
        onChange={handleFileChange}
      />

      {error ? (
        <p id={`${inputId}-error`} className="text-caption text-danger" role="alert">
          {error}
        </p>
      ) : (
        <p id={`${inputId}-helper`} className="text-caption text-ink-muted">
          Your photo is stored securely and used only for access verification.
        </p>
      )}
    </div>
  );
}
